import React, { useRef, useState } from 'react';
import {
  Box,
  Flex,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAgriculture,
  MdApartment,
  MdCloudUpload,
  MdDownload,
  MdPeopleAlt,
  MdHandyman,
} from 'react-icons/md';
import {
  commitBuSitesImport,
  commitEquipmentTypesImport,
  commitUserEquipmentImport,
  commitUsersImport,
  downloadBuSitesImportTemplate,
  downloadEquipmentTypesImportTemplate,
  downloadUserEquipmentImportTemplate,
  downloadUsersImportTemplate,
  validateBuSitesImport,
  validateEquipmentTypesImport,
  validateUserEquipmentImport,
  validateUsersImport,
} from '../../../repositories/bulkImport.repository';
import { downloadEquipmentTypesImportErrorRows } from '../../../utils/equipmentTypesImportErrorsExcel';
import {
  downloadBuSitesImportErrorRows,
  downloadUserEquipmentImportErrorRows,
  downloadUsersImportErrorRows,
} from '../../../utils/bulkImportErrorExcel';
import {
  HubAmberButton,
  HubGhostButton,
  HubPanel,
  HubSectionLabel,
  HubTable,
  hub,
  useHubHeaderActions,
} from './hubUi';

const STAGES = [
  {
    id: 'equipmentTypes',
    label: 'Equipment types',
    hint: 'Download template → upload → add',
    icon: MdAgriculture,
  },
  {
    id: 'businessUnits',
    label: 'BUs & sites',
    hint: 'Download template → upload → add',
    icon: MdApartment,
  },
  {
    id: 'users',
    label: 'Users',
    hint: 'Download template → upload → add or update',
    icon: MdPeopleAlt,
  },
  {
    id: 'userEquipment',
    label: 'User equipment',
    hint: 'Download template → upload → add or update',
    icon: MdHandyman,
  },
];

function ImportStageCard({ label, hint, icon: Icon, onClick }) {
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="100%"
      h="100%"
      bg={hub.cardBg}
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="12px"
      px={3.5}
      py={3.5}
      cursor="pointer"
      transition="border-color 0.15s, transform 0.15s"
      _hover={{ borderColor: 'utkarsh.amber', transform: 'translateY(-1px)' }}
      onClick={onClick}
      sx={{ '& > *': { pointerEvents: 'none' } }}
    >
      <Flex align="center" gap={3} w="100%">
        <Flex
          align="center"
          justify="center"
          w="44px"
          h="44px"
          borderRadius="10px"
          bg="bg.input"
          color="utkarsh.amber"
          flexShrink={0}
        >
          <Icon size={22} />
        </Flex>
        <Box minW={0} flex="1">
          <Text
            fontSize={{ base: '16px', md: '17px' }}
            fontWeight="700"
            color="text.primary"
            letterSpacing="-0.02em"
            lineHeight="1.25"
            noOfLines={2}
            mb={1}
          >
            {label}
          </Text>
          <Text fontSize="13px" color={hub.muted} noOfLines={2} lineHeight="1.35">
            {hint}
          </Text>
        </Box>
        <Text
          fontSize="13px"
          fontWeight="600"
          color="utkarsh.amber"
          flexShrink={0}
        >
          Import →
        </Text>
      </Flex>
    </Box>
  );
}

function ImportGuide({ steps, columns }) {
  return (
    <HubPanel title="How it works">
      <VStack align="stretch" spacing={3}>
        <VStack align="stretch" spacing={1}>
          {steps.map((step, i) => (
            <Text key={i} fontSize="13px" color={hub.muted} lineHeight="1.45">
              <Text as="span" fontWeight="600" color="text.primary">
                {i + 1}.
              </Text>{' '}
              {step}
            </Text>
          ))}
        </VStack>
        {columns?.length ? (
          <Box
            overflowX="auto"
            border="1px solid"
            borderColor={hub.cardBorder}
            borderRadius="8px"
          >
            <Table size="sm" variant="simple">
              <Thead bg="bg.input">
                <Tr>
                  <Th fontSize="11px" color={hub.muted} py={2} whiteSpace="nowrap">
                    Column
                  </Th>
                  <Th fontSize="11px" color={hub.muted} py={2} whiteSpace="nowrap">
                    Required / Optional
                  </Th>
                  <Th fontSize="11px" color={hub.muted} py={2} whiteSpace="nowrap">
                    Unique
                  </Th>
                  <Th fontSize="11px" color={hub.muted} py={2}>
                    Notes
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {columns.map((row) => (
                  <Tr key={row.column}>
                    <Td
                      fontSize="13px"
                      fontWeight="600"
                      color="text.primary"
                      py={2}
                      whiteSpace="nowrap"
                    >
                      {row.column}
                    </Td>
                    <Td fontSize="13px" color="text.primary" py={2} whiteSpace="nowrap">
                      {row.required}
                    </Td>
                    <Td fontSize="13px" color="text.primary" py={2} whiteSpace="nowrap">
                      {row.unique}
                    </Td>
                    <Td fontSize="13px" color={hub.muted} py={2}>
                      {row.notes || '—'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : null}
        <Text fontSize="12px" color={hub.muted}>
          Full column list is also on the Instructions sheet inside the downloaded
          template.
        </Text>
      </VStack>
    </HubPanel>
  );
}

function statusCell(r) {
  return (
    <Text
      fontSize="13px"
      fontWeight="600"
      color={r.status === 'ok' ? 'green.500' : 'red.400'}
    >
      {r.status === 'ok' ? 'OK' : 'Error'}
    </Text>
  );
}

function errorsCell(r) {
  const list = Array.isArray(r?.errors)
    ? r.errors.filter((e) => e != null && String(e).trim() !== '')
    : r?.errors
      ? [String(r.errors)]
      : [];
  return list.length ? (
    <Text fontSize="12px" color="red.400" whiteSpace="normal">
      {list.join('; ')}
    </Text>
  ) : (
    '—'
  );
}

export function HubBulkImportHub({ push }) {
  return (
    <>
      <HubSectionLabel>Import stages</HubSectionLabel>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={3}>
        {STAGES.map((stage) => (
          <ImportStageCard
            key={stage.id}
            icon={stage.icon}
            label={stage.label}
            hint={stage.hint}
            onClick={() =>
              push({
                id: 'bulkImportStage',
                label: stage.label,
                stageId: stage.id,
              })
            }
          />
        ))}
      </SimpleGrid>
    </>
  );
}

export function HubBulkImportStage({ stageId }) {
  if (stageId === 'equipmentTypes') {
    return <EquipmentTypesImportStage />;
  }
  if (stageId === 'businessUnits') {
    return <BuSitesImportStage />;
  }
  if (stageId === 'users') {
    return <UsersImportStage />;
  }
  if (stageId === 'userEquipment') {
    return <UserEquipmentImportStage />;
  }
  return (
    <HubPanel>
      <Text color={hub.muted}>This import stage is not available yet.</Text>
    </HubPanel>
  );
}

function EquipmentTypesImportStage() {
  const toast = useToast();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);

  const rows = preview?.rows || [];
  const summary = preview?.summary || null;
  const validRows = rows.filter((r) => r.status === 'ok' && r.data);
  const errorRows = rows.filter((r) => r.status === 'error');
  const canAddValid = validRows.length > 0;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadEquipmentTypesImportTemplate();
      toast({ title: 'Template downloaded', status: 'success', duration: 2000 });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadErrors = () => {
    try {
      downloadEquipmentTypesImportErrorRows(errorRows);
      toast({
        title: `Downloaded ${errorRows.length} error row(s)`,
        status: 'success',
        duration: 2500,
      });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    }
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setValidating(true);
    setPreview(null);
    setFileName(file.name);
    try {
      const result = await validateEquipmentTypesImport(file);
      const withIds = {
        ...result,
        rows: (result?.rows || []).map((r) => ({
          ...r,
          id: r.rowNumber,
        })),
      };
      setPreview(withIds);
      const inv = withIds?.summary?.invalid ?? 0;
      toast({
        title:
          inv > 0
            ? `Validated with ${inv} error(s)`
            : `Validated ${withIds?.summary?.valid ?? 0} row(s)`,
        status: inv > 0 ? 'warning' : 'success',
        duration: 3000,
      });
    } catch (e) {
      setFileName('');
      toast({ title: e.message || 'Validation failed', status: 'error' });
    } finally {
      setValidating(false);
    }
  };

  const handleCommitValid = async () => {
    if (!canAddValid) return;
    setCommitting(true);
    try {
      const payload = validRows.map((r) => ({
        name: r.data.name,
        category: r.data.category ?? null,
        vehicleClass: r.data.vehicleClass ?? null,
        description: r.data.description ?? null,
        isActive: r.data.isActive !== false,
        fieldRequirements: r.data.fieldRequirements || {},
        docRequirements: r.data.docRequirements || {},
      }));
      const result = await commitEquipmentTypesImport(payload);
      toast({
        title: `Added ${result?.created ?? 0} equipment type(s)`,
        status: 'success',
        duration: 3000,
      });
      if (errorRows.length > 0) {
        setPreview({
          rows: errorRows,
          summary: {
            total: errorRows.length,
            valid: 0,
            invalid: errorRows.length,
          },
        });
      } else {
        setPreview(null);
        setFileName('');
      }
    } catch (e) {
      toast({ title: e.message || 'Commit failed', status: 'error' });
    } finally {
      setCommitting(false);
    }
  };

  const columns = [
    { key: 'rowNumber', label: 'Row', render: (r) => r.rowNumber },
    { key: 'status', label: 'Status', render: statusCell },
    { key: 'name', label: 'Name', render: (r) => r.data?.name || '—' },
    {
      key: 'category',
      label: 'Category',
      render: (r) => r.data?.category || '—',
    },
    {
      key: 'vehicleClass',
      label: 'Vehicle class',
      render: (r) => r.data?.vehicleClass || '—',
    },
    {
      key: 'isActive',
      label: 'Active',
      render: (r) =>
        r.data == null ? '—' : r.data.isActive === false ? 'No' : 'Yes',
    },
    {
      key: 'fields',
      label: 'Form fields',
      render: (r) => {
        const fr = r.data?.fieldRequirements || {};
        const req = Object.values(fr).filter((v) => v === 'required').length;
        const opt = Object.values(fr).filter((v) => v === 'optional').length;
        const hide = Object.values(fr).filter((v) => v === 'hidden').length;
        return `Req ${req} · Opt ${opt} · Hide ${hide}`;
      },
    },
    {
      key: 'docs',
      label: 'Documents',
      render: (r) => {
        const dr = r.data?.docRequirements || {};
        const custom = Array.isArray(dr.customDocs) ? dr.customDocs.length : 0;
        const levels = Object.entries(dr).filter(([k]) => k !== 'customDocs');
        const req = levels.filter(([, v]) => v === 'required').length;
        return `Req ${req} · Custom ${custom}`;
      },
    },
    { key: 'errors', label: 'Errors', render: errorsCell },
  ];

  useHubHeaderActions(
    () => (
      <>
        <HubGhostButton
          leftIcon={<MdDownload size={16} />}
          onClick={handleDownload}
          isLoading={downloading}
        >
          Download template
        </HubGhostButton>
        <HubAmberButton
          leftIcon={<MdCloudUpload size={16} />}
          onClick={() => fileRef.current?.click()}
          isLoading={validating}
        >
          Upload Excel
        </HubAmberButton>
      </>
    ),
    [downloading, validating],
  );

  return (
    <>
      <Input
        ref={fileRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        display="none"
        onChange={handleFilePicked}
      />

      <ImportGuide
        steps={[
          'Download the template, then fill the EquipmentTypes sheet (row 1 = sections, row 2 = column names).',
          'Upload the file. Valid rows appear first; error rows below. Add valid rows now, and download error rows to fix.',
        ]}
        columns={[
          {
            column: 'name',
            required: 'Required',
            unique: 'Yes',
            notes: 'Equipment type name',
          },
          {
            column: 'category',
            required: 'Optional',
            unique: 'No',
            notes: 'e.g. Construction',
          },
          {
            column: 'vehicleClass',
            required: 'Optional',
            unique: 'No',
            notes:
              'LMV / HMV / Construction Equipment / Stationary Equipment',
          },
          {
            column: 'Field / Document columns',
            required: 'Optional',
            unique: 'No',
            notes: 'Use required, optional, or hidden',
          },
          {
            column: 'customDoc1–4',
            required: 'Optional',
            unique: 'No',
            notes: 'Name:required or Name:optional',
          },
          {
            column: 'isActive',
            required: 'Optional',
            unique: 'No',
            notes: 'Yes / No · blank = Yes',
          },
        ]}
      />

      {summary ? (
        <Flex gap={3} flexWrap="wrap" my={3} align="center">
          <Text fontSize="13px" color={hub.muted}>
            File: {fileName || '—'}
          </Text>
          <Text fontSize="13px" fontWeight="600">
            {summary.total} total
          </Text>
          <Text fontSize="13px" color="green.500" fontWeight="600">
            {summary.valid} without error
          </Text>
          <Text fontSize="13px" color="red.400" fontWeight="600">
            {summary.invalid} with error
          </Text>
          <Box flex="1" />
          {errorRows.length > 0 ? (
            <HubGhostButton
              leftIcon={<MdDownload size={16} />}
              onClick={handleDownloadErrors}
            >
              Download error rows ({errorRows.length})
            </HubGhostButton>
          ) : null}
          <HubAmberButton
            onClick={handleCommitValid}
            isDisabled={!canAddValid}
            isLoading={committing}
          >
            {errorRows.length > 0
              ? `Add valid rows (${validRows.length})`
              : 'Add this data'}
          </HubAmberButton>
        </Flex>
      ) : (
        <Text fontSize="13px" color={hub.muted} my={3}>
          Upload a filled template to preview rows here.
        </Text>
      )}

      {validRows.length > 0 ? (
        <Box mb={4}>
          <HubSectionLabel>
            Without error ({validRows.length})
          </HubSectionLabel>
          <HubTable columns={columns} rows={validRows} />
        </Box>
      ) : null}

      {errorRows.length > 0 ? (
        <Box mb={2}>
          <HubSectionLabel>
            With error ({errorRows.length})
          </HubSectionLabel>
          <HubTable columns={columns} rows={errorRows} />
        </Box>
      ) : null}
    </>
  );
}

function BuSitesImportStage() {
  const toast = useToast();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);

  const buRows = preview?.businessUnits?.rows || [];
  const siteRows = preview?.sites?.rows || [];
  const buSummary = preview?.businessUnits?.summary || null;
  const siteSummary = preview?.sites?.summary || null;

  const buValidRows = buRows.filter((r) => r.status === 'ok' && r.data);
  const buErrorRows = buRows.filter((r) => r.status === 'error');
  const siteValidRows = siteRows.filter((r) => r.status === 'ok' && r.data);
  const siteErrorRows = siteRows.filter((r) => r.status === 'error');
  const totalErrors = buErrorRows.length + siteErrorRows.length;
  const canAddValid = buValidRows.length > 0 || siteValidRows.length > 0;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadBuSitesImportTemplate();
      toast({ title: 'Template downloaded', status: 'success', duration: 2000 });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadErrors = () => {
    try {
      downloadBuSitesImportErrorRows({
        buErrorRows,
        siteErrorRows,
      });
      toast({
        title: `Downloaded ${totalErrors} error row(s)`,
        status: 'success',
        duration: 2500,
      });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    }
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setValidating(true);
    setPreview(null);
    setFileName(file.name);
    try {
      const result = await validateBuSitesImport(file);
      const withIds = {
        businessUnits: {
          ...result.businessUnits,
          rows: (result?.businessUnits?.rows || []).map((r) => ({
            ...r,
            id: `bu-${r.rowNumber}`,
          })),
        },
        sites: {
          ...result.sites,
          rows: (result?.sites?.rows || []).map((r) => ({
            ...r,
            id: `site-${r.rowNumber}`,
          })),
        },
      };
      setPreview(withIds);
      const inv =
        (withIds.businessUnits?.summary?.invalid || 0) +
        (withIds.sites?.summary?.invalid || 0);
      toast({
        title:
          inv > 0
            ? `Validated with ${inv} error(s)`
            : `Validated ${withIds.businessUnits?.summary?.valid || 0} BU(s), ${
                withIds.sites?.summary?.valid || 0
              } site(s)`,
        status: inv > 0 ? 'warning' : 'success',
        duration: 3000,
      });
    } catch (e) {
      setFileName('');
      toast({ title: e.message || 'Validation failed', status: 'error' });
    } finally {
      setValidating(false);
    }
  };

  const handleCommitValid = async () => {
    if (!canAddValid) return;
    setCommitting(true);
    try {
      const result = await commitBuSitesImport({
        businessUnits: buValidRows.map((r) => ({
          shortName: r.data.shortName,
          fullNameGst: r.data.fullNameGst ?? null,
          address1: r.data.address1 ?? null,
          address2: r.data.address2 ?? null,
          locality: r.data.locality ?? null,
          sublocality: r.data.sublocality ?? null,
          taluka: r.data.taluka ?? null,
          district: r.data.district ?? null,
          state: r.data.state ?? null,
          country: r.data.country ?? null,
          pin: r.data.pin ?? null,
          latitude: r.data.latitude ?? null,
          longitude: r.data.longitude ?? null,
          villageCode: r.data.villageCode ?? null,
          villageName: r.data.villageName ?? null,
          villageId: r.data.villageId ?? null,
          isActive: r.data.isActive !== false,
        })),
        sites: siteValidRows.map((r) => ({
          buShortName: r.data.buShortName,
          siteName: r.data.siteName,
          projectOperation: r.data.projectOperation ?? null,
          address1: r.data.address1 ?? null,
          address2: r.data.address2 ?? null,
          locality: r.data.locality ?? null,
          sublocality: r.data.sublocality ?? null,
          taluka: r.data.taluka ?? null,
          district: r.data.district ?? null,
          state: r.data.state ?? null,
          country: r.data.country ?? null,
          pin: r.data.pin ?? null,
          latitude: r.data.latitude ?? null,
          longitude: r.data.longitude ?? null,
          villageCode: r.data.villageCode ?? null,
          villageName: r.data.villageName ?? null,
          villageId: r.data.villageId ?? null,
          pinOverride: r.data.pinOverride ?? null,
          isActive: r.data.isActive !== false,
        })),
      });
      toast({
        title: `Added ${result?.createdBus ?? 0} BU(s) and ${
          result?.createdSites ?? 0
        } site(s)`,
        status: 'success',
        duration: 3000,
      });
      if (totalErrors > 0) {
        setPreview({
          businessUnits: {
            rows: buErrorRows,
            summary: {
              total: buErrorRows.length,
              valid: 0,
              invalid: buErrorRows.length,
            },
          },
          sites: {
            rows: siteErrorRows,
            summary: {
              total: siteErrorRows.length,
              valid: 0,
              invalid: siteErrorRows.length,
            },
          },
        });
      } else {
        setPreview(null);
        setFileName('');
      }
    } catch (e) {
      toast({ title: e.message || 'Commit failed', status: 'error' });
    } finally {
      setCommitting(false);
    }
  };

  const buColumns = [
    { key: 'rowNumber', label: 'Row', render: (r) => r.rowNumber },
    { key: 'status', label: 'Status', render: statusCell },
    {
      key: 'shortName',
      label: 'Short name',
      render: (r) => r.data?.shortName || '—',
    },
    {
      key: 'locality',
      label: 'Locality',
      render: (r) => r.data?.locality || r.data?.address1 || '—',
    },
    {
      key: 'village',
      label: 'Village',
      render: (r) =>
        r.data?.villageName || r.data?.villageCode || r.data?.villageId || '—',
    },
    { key: 'errors', label: 'Errors', render: errorsCell },
  ];

  const siteColumns = [
    { key: 'rowNumber', label: 'Row', render: (r) => r.rowNumber },
    { key: 'status', label: 'Status', render: statusCell },
    {
      key: 'buShortName',
      label: 'BU',
      render: (r) => r.data?.buShortName || '—',
    },
    {
      key: 'siteName',
      label: 'Site',
      render: (r) => r.data?.siteName || '—',
    },
    {
      key: 'projectOperation',
      label: 'Type',
      render: (r) => r.data?.projectOperation || '—',
    },
    {
      key: 'locality',
      label: 'Locality',
      render: (r) => r.data?.locality || r.data?.address1 || '—',
    },
    { key: 'errors', label: 'Errors', render: errorsCell },
  ];

  useHubHeaderActions(
    () => (
      <>
        <HubGhostButton
          leftIcon={<MdDownload size={16} />}
          onClick={handleDownload}
          isLoading={downloading}
        >
          Download template
        </HubGhostButton>
        <HubAmberButton
          leftIcon={<MdCloudUpload size={16} />}
          onClick={() => fileRef.current?.click()}
          isLoading={validating}
        >
          Upload Excel
        </HubAmberButton>
      </>
    ),
    [downloading, validating],
  );

  return (
    <>
      <Input
        ref={fileRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        display="none"
        onChange={handleFilePicked}
      />

      <ImportGuide
        steps={[
          'Download the template. Fill BusinessUnits first, then Sites (link with buShortName).',
          'Upload. Valid rows appear first; error rows below. Add valid rows now, and download error rows to fix.',
        ]}
        columns={[
          {
            column: 'shortName (BU)',
            required: 'Required',
            unique: 'Yes',
            notes: 'Business unit short name',
          },
          {
            column: 'address1 / locality',
            required: 'Required*',
            unique: 'No',
            notes: '*At least one location field',
          },
          {
            column: 'buShortName (Site)',
            required: 'Required',
            unique: 'No',
            notes: 'Must match a BU shortName',
          },
          {
            column: 'siteName',
            required: 'Required',
            unique: 'No',
            notes: 'Site under that BU',
          },
          {
            column: 'projectOperation',
            required: 'Optional',
            unique: 'No',
            notes: 'Project or Operation',
          },
          {
            column: 'isActive',
            required: 'Optional',
            unique: 'No',
            notes: 'Yes / No · blank = Yes',
          },
        ]}
      />

      {preview ? (
        <Flex gap={3} flexWrap="wrap" my={3} align="center">
          <Text fontSize="13px" color={hub.muted}>
            File: {fileName || '—'}
          </Text>
          <Text fontSize="13px" fontWeight="600">
            {(buSummary?.total || 0) + (siteSummary?.total || 0)} total
          </Text>
          <Text fontSize="13px" color="green.500" fontWeight="600">
            {(buSummary?.valid || 0) + (siteSummary?.valid || 0)} without error
          </Text>
          <Text fontSize="13px" color="red.400" fontWeight="600">
            {totalErrors} with error
          </Text>
          <Box flex="1" />
          {totalErrors > 0 ? (
            <HubGhostButton
              leftIcon={<MdDownload size={16} />}
              onClick={handleDownloadErrors}
            >
              Download error rows ({totalErrors})
            </HubGhostButton>
          ) : null}
          <HubAmberButton
            onClick={handleCommitValid}
            isDisabled={!canAddValid}
            isLoading={committing}
          >
            {totalErrors > 0
              ? `Add valid rows (${buValidRows.length + siteValidRows.length})`
              : 'Add this data'}
          </HubAmberButton>
        </Flex>
      ) : (
        <Text fontSize="13px" color={hub.muted} my={3}>
          Upload a filled template to preview rows here.
        </Text>
      )}

      {buValidRows.length > 0 ? (
        <Box mb={4}>
          <HubSectionLabel>
            Business units without error ({buValidRows.length})
          </HubSectionLabel>
          <HubTable columns={buColumns} rows={buValidRows} />
        </Box>
      ) : null}

      {siteValidRows.length > 0 ? (
        <Box mb={4}>
          <HubSectionLabel>
            Sites without error ({siteValidRows.length})
          </HubSectionLabel>
          <HubTable columns={siteColumns} rows={siteValidRows} />
        </Box>
      ) : null}

      {buErrorRows.length > 0 ? (
        <Box mb={4}>
          <HubSectionLabel>
            Business units with error ({buErrorRows.length})
          </HubSectionLabel>
          <HubTable columns={buColumns} rows={buErrorRows} />
        </Box>
      ) : null}

      {siteErrorRows.length > 0 ? (
        <Box mb={2}>
          <HubSectionLabel>
            Sites with error ({siteErrorRows.length})
          </HubSectionLabel>
          <HubTable columns={siteColumns} rows={siteErrorRows} />
        </Box>
      ) : null}
    </>
  );
}

function userActionCell(r) {
  if (r.status !== 'ok') {
    return (
      <Text fontSize="13px" fontWeight="600" color="red.400">
        Error
      </Text>
    );
  }
  if (r.action === 'update') {
    return (
      <Text fontSize="13px" fontWeight="600" color="orange.500">
        Already in system
      </Text>
    );
  }
  return (
    <Text fontSize="13px" fontWeight="600" color="green.500">
      New
    </Text>
  );
}

function UsersImportStage() {
  const toast = useToast();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);

  const userRows = preview?.users?.rows || [];
  const linkRows = preview?.buyerBusinessUnits?.rows || [];
  const userSummary = preview?.users?.summary || null;
  const linkSummary = preview?.buyerBusinessUnits?.summary || null;

  const newRows = userRows.filter(
    (r) => r.status === 'ok' && r.action === 'create' && r.data,
  );
  const existingRows = userRows.filter(
    (r) => r.status === 'ok' && r.action === 'update' && r.data,
  );
  const errorRows = userRows.filter((r) => r.status === 'error');
  const linkValidRows = linkRows.filter((r) => r.status === 'ok' && r.data);
  const linkErrorRows = linkRows.filter((r) => r.status === 'error');
  const totalErrors = errorRows.length + linkErrorRows.length;

  const handleDownloadErrors = () => {
    try {
      downloadUsersImportErrorRows({
        userErrorRows: errorRows,
        linkErrorRows,
      });
      toast({
        title: `Downloaded ${totalErrors} error row(s)`,
        status: 'success',
        duration: 2500,
      });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    }
  };

  const mapUserPayload = (r) => ({
    phone: r.data.phone,
    email: r.data.email ?? null,
    firstName: r.data.firstName,
    lastName: r.data.lastName,
    middleName: r.data.middleName ?? null,
    gender: r.data.gender ?? null,
    dob: r.data.dob ?? null,
    address1: r.data.address1 ?? null,
    address2: r.data.address2 ?? null,
    village: r.data.village ?? null,
    taluka: r.data.taluka ?? null,
    district: r.data.district ?? null,
    state: r.data.state ?? null,
    pin: r.data.pin ?? null,
    role: r.data.role,
    tradeName: r.data.tradeName ?? null,
    gstNumber: r.data.gstNumber ?? null,
    companyEmail: r.data.companyEmail ?? null,
    constitution: r.data.constitution ?? null,
    employeeCode: r.data.employeeCode ?? null,
    aadhaarNumber: r.data.aadhaarNumber ?? null,
    panNumber: r.data.panNumber ?? null,
    offerings: r.data.offerings || [],
    specialServices: r.data.specialServices || [],
    buyerVerified:
      r.data.buyerVerificationStatus === 'verified' ? 'Yes' : 'No',
    sellerVerified:
      r.data.sellerVerificationStatus === 'verified' ? 'Yes' : 'No',
    buyerVerificationStatus: r.data.buyerVerificationStatus,
    sellerVerificationStatus: r.data.sellerVerificationStatus,
    isActive: r.data.isActive !== false,
  });

  const mapLinkPayload = (r) => ({
    phone: r.data.phone,
    buShortName: r.data.buShortName,
    siteName: r.data.siteName ?? null,
    isPrimary: Boolean(r.data.isPrimary),
    buId: r.data.buId ?? null,
    siteId: r.data.siteId ?? null,
  });

  const linksForPhones = (rows) => {
    const phones = new Set(rows.map((r) => r.data.phone));
    return linkRows.filter(
      (r) => r.status === 'ok' && r.data && phones.has(r.data.phone),
    );
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadUsersImportTemplate();
      toast({ title: 'Template downloaded', status: 'success', duration: 2000 });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setValidating(true);
    setPreview(null);
    setFileName(file.name);
    try {
      const result = await validateUsersImport(file);
      const withIds = {
        users: {
          ...result.users,
          rows: (result?.users?.rows || []).map((r) => ({
            ...r,
            id: `user-${r.rowNumber}`,
          })),
        },
        buyerBusinessUnits: {
          ...result.buyerBusinessUnits,
          rows: (result?.buyerBusinessUnits?.rows || []).map((r) => ({
            ...r,
            id: `link-${r.rowNumber}`,
          })),
        },
      };
      setPreview(withIds);
      const createN = withIds.users?.summary?.createValid ?? 0;
      const updateN = withIds.users?.summary?.updateValid ?? 0;
      const inv =
        (withIds.users?.summary?.invalid || 0) +
        (withIds.buyerBusinessUnits?.summary?.invalid || 0);
      toast({
        title:
          inv > 0
            ? `Validated: ${createN} new, ${updateN} existing, ${inv} error(s)`
            : `Validated: ${createN} new, ${updateN} already in system`,
        status: inv > 0 ? 'warning' : 'success',
        duration: 3500,
      });
    } catch (e) {
      setFileName('');
      toast({ title: e.message || 'Validation failed', status: 'error' });
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async (mode) => {
    const rows = mode === 'update' ? existingRows : newRows;
    if (!rows.length) return;
    const phones = new Set(rows.map((r) => r.data.phone));
    const hasLinkErrors = linkRows.some(
      (r) => r.data && phones.has(r.data.phone) && r.status === 'error',
    );
    if (hasLinkErrors) {
      toast({
        title: 'Fix BuyerBusinessUnits errors for these phones first',
        status: 'error',
      });
      return;
    }
    const setBusy = mode === 'update' ? setUpdating : setAdding;
    setBusy(true);
    try {
      const result = await commitUsersImport({
        mode,
        users: rows.map(mapUserPayload),
        buyerBusinessUnits: linksForPhones(rows).map(mapLinkPayload),
      });
      toast({
        title:
          mode === 'update'
            ? `Updated ${result?.updatedUsers ?? 0} user(s)`
            : `Added ${result?.createdUsers ?? 0} user(s)`,
        status: 'success',
        duration: 3000,
      });
      const keepAction = mode === 'update' ? 'create' : 'update';
      const remainingUsers = (preview?.users?.rows || []).filter(
        (r) => r.status === 'error' || r.action === keepAction,
      );
      const remainingPhones = new Set(
        remainingUsers
          .filter((r) => r.data?.phone)
          .map((r) => r.data?.phone),
      );
      const remainingLinks = (preview?.buyerBusinessUnits?.rows || []).filter(
        (r) =>
          r.status === 'error' ||
          (r.data?.phone && remainingPhones.has(r.data.phone)),
      );
      if (!remainingUsers.length && !remainingLinks.length) {
        setPreview(null);
        setFileName('');
      } else {
        setPreview({
          users: {
            rows: remainingUsers,
            summary: {
              total: remainingUsers.length,
              valid: remainingUsers.filter((r) => r.status === 'ok').length,
              invalid: remainingUsers.filter((r) => r.status === 'error')
                .length,
              createValid: remainingUsers.filter(
                (r) => r.status === 'ok' && r.action === 'create',
              ).length,
              updateValid: remainingUsers.filter(
                (r) => r.status === 'ok' && r.action === 'update',
              ).length,
            },
          },
          buyerBusinessUnits: {
            rows: remainingLinks,
            summary: {
              total: remainingLinks.length,
              valid: remainingLinks.filter((r) => r.status === 'ok').length,
              invalid: remainingLinks.filter((r) => r.status === 'error')
                .length,
            },
          },
        });
      }
    } catch (e) {
      toast({ title: e.message || 'Save failed', status: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const userColumns = [
    { key: 'rowNumber', label: 'Row', render: (r) => r.rowNumber },
    { key: 'status', label: 'Status', render: userActionCell },
    { key: 'phone', label: 'Phone', render: (r) => r.data?.phone || '—' },
    {
      key: 'aadhaar',
      label: 'Aadhaar',
      render: (r) => r.data?.aadhaarNumber || '—',
    },
    {
      key: 'name',
      label: 'Name',
      render: (r) =>
        [r.data?.firstName, r.data?.lastName].filter(Boolean).join(' ') || '—',
    },
    { key: 'role', label: 'Detected role', render: (r) => r.data?.role || '—' },
    {
      key: 'verified',
      label: 'Verified',
      render: (r) => {
        const parts = [];
        const role = r.data?.role;
        if (role === 'agency' || role === 'both') {
          parts.push(
            `Buyer: ${
              r.data?.buyerVerificationStatus === 'verified' ? 'Yes' : 'No'
            }`,
          );
        }
        if (role === 'service_provider' || role === 'both') {
          parts.push(
            `Seller: ${
              r.data?.sellerVerificationStatus === 'verified' ? 'Yes' : 'No'
            }`,
          );
        }
        return parts.join(' · ') || '—';
      },
    },
    {
      key: 'tradeName',
      label: 'Trade / offerings',
      render: (r) =>
        r.data?.tradeName ||
        (r.data?.offerings?.length ? r.data.offerings.join(', ') : '—'),
    },
    { key: 'errors', label: 'Errors', render: errorsCell },
  ];

  const linkColumns = [
    { key: 'rowNumber', label: 'Row', render: (r) => r.rowNumber },
    { key: 'status', label: 'Status', render: statusCell },
    { key: 'phone', label: 'Phone', render: (r) => r.data?.phone || '—' },
    {
      key: 'buShortName',
      label: 'BU',
      render: (r) => r.data?.buShortName || '—',
    },
    {
      key: 'siteName',
      label: 'Site',
      render: (r) => r.data?.siteName || '—',
    },
    { key: 'errors', label: 'Errors', render: errorsCell },
  ];

  useHubHeaderActions(
    () => (
      <>
        <HubGhostButton
          leftIcon={<MdDownload size={16} />}
          onClick={handleDownload}
          isLoading={downloading}
        >
          Download template
        </HubGhostButton>
        <HubAmberButton
          leftIcon={<MdCloudUpload size={16} />}
          onClick={() => fileRef.current?.click()}
          isLoading={validating}
        >
          Upload Excel
        </HubAmberButton>
      </>
    ),
    [downloading, validating],
  );

  return (
    <>
      <Input
        ref={fileRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        display="none"
        onChange={handleFilePicked}
      />

      <ImportGuide
        steps={[
          'Download the template and fill one person per Users row. Buyers also use BuyerBusinessUnits.',
          'After upload: New → Add new users. Already in system → Update. Download error rows to fix and re-upload.',
          'Buyer = tradeName + gstNumber (both required). Seller = offerings only — leave tradeName, gstNumber, and companyEmail blank (GST is not used for sellers). Both roles = fill buyer fields and offerings.',
        ]}
        columns={[
          {
            column: 'phone',
            required: 'Required',
            unique: 'Yes',
            notes: '10 digits · matches for Update',
          },
          {
            column: 'aadhaarNumber',
            required: 'Required',
            unique: 'Yes',
            notes: '12 digits',
          },
          {
            column: 'firstName / lastName',
            required: 'Required',
            unique: 'No',
            notes: 'Personal name',
          },
          {
            column: 'address1 / village',
            required: 'Required*',
            unique: 'No',
            notes: '*At least one',
          },
          {
            column: 'email',
            required: 'Optional',
            unique: 'Yes*',
            notes: '*Unique if filled',
          },
          {
            column: 'tradeName / gstNumber',
            required: 'Required for buyer',
            unique: 'No',
            notes: 'Buyer only — leave blank for sellers',
          },
          {
            column: 'offerings',
            required: 'Required for seller',
            unique: 'No',
            notes: 'e.g. equipment;material_supply',
          },
          {
            column: 'buShortName (BU sheet)',
            required: 'Required for buyer link',
            unique: 'No',
            notes: 'Existing BU short name',
          },
        ]}
      />

      {preview ? (
        <Flex gap={3} flexWrap="wrap" my={3} align="center">
          <Text fontSize="13px" color={hub.muted}>
            File: {fileName || '—'}
          </Text>
          <Text fontSize="13px" fontWeight="600">
            {userSummary?.total ?? userRows.length} total
          </Text>
          <Text fontSize="13px" fontWeight="600" color="green.600">
            New {userSummary?.createValid ?? newRows.length}
          </Text>
          <Text fontSize="13px" fontWeight="600" color="orange.500">
            Already in system {userSummary?.updateValid ?? existingRows.length}
          </Text>
          <Text fontSize="13px" color="red.400" fontWeight="600">
            {totalErrors} with error
          </Text>
          <Box flex="1" />
          {totalErrors > 0 ? (
            <HubGhostButton
              leftIcon={<MdDownload size={16} />}
              onClick={handleDownloadErrors}
            >
              Download error rows ({totalErrors})
            </HubGhostButton>
          ) : null}
        </Flex>
      ) : (
        <Text fontSize="13px" color={hub.muted} my={3}>
          Upload a filled template to preview rows here.
        </Text>
      )}

      {newRows.length > 0 ? (
        <>
          <Flex align="center" gap={3} mb={2} flexWrap="wrap">
            <HubSectionLabel>New users ({newRows.length})</HubSectionLabel>
            <Box flex="1" />
            <HubAmberButton
              onClick={() => handleCommit('create')}
              isLoading={adding}
              isDisabled={updating}
            >
              Add new users
            </HubAmberButton>
          </Flex>
          <Box mb={4}>
            <HubTable columns={userColumns} rows={newRows} />
          </Box>
        </>
      ) : null}

      {existingRows.length > 0 ? (
        <>
          <Flex align="center" gap={3} mb={2} flexWrap="wrap">
            <HubSectionLabel>
              Already in system ({existingRows.length})
            </HubSectionLabel>
            <Box flex="1" />
            <HubAmberButton
              onClick={() => handleCommit('update')}
              isLoading={updating}
              isDisabled={adding}
            >
              Update these users
            </HubAmberButton>
          </Flex>
          <Box mb={4}>
            <HubTable columns={userColumns} rows={existingRows} />
          </Box>
        </>
      ) : null}

      {linkValidRows.length > 0 ? (
        <Box mb={4}>
          <HubSectionLabel>
            Buyer business units without error ({linkValidRows.length})
          </HubSectionLabel>
          <HubTable columns={linkColumns} rows={linkValidRows} />
        </Box>
      ) : null}

      {errorRows.length > 0 ? (
        <Box mb={4}>
          <HubSectionLabel>
            Users with error ({errorRows.length})
          </HubSectionLabel>
          <HubTable columns={userColumns} rows={errorRows} />
        </Box>
      ) : null}

      {linkErrorRows.length > 0 ? (
        <Box mb={2}>
          <HubSectionLabel>
            Buyer business units with error ({linkErrorRows.length})
          </HubSectionLabel>
          <HubTable columns={linkColumns} rows={linkErrorRows} />
        </Box>
      ) : null}
    </>
  );
}

function equipmentActionCell(r) {
  if (r.status !== 'ok') {
    return (
      <Text fontSize="13px" fontWeight="600" color="red.400">
        Error
      </Text>
    );
  }
  if (r.action === 'update') {
    return (
      <Text fontSize="13px" fontWeight="600" color="orange.500">
        Already in system
      </Text>
    );
  }
  return (
    <Text fontSize="13px" fontWeight="600" color="green.500">
      New
    </Text>
  );
}

function UserEquipmentImportStage() {
  const toast = useToast();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);

  const rows = preview?.rows || [];
  const summary = preview?.summary || null;
  const newRows = rows.filter(
    (r) => r.status === 'ok' && r.action === 'create' && r.data,
  );
  const existingRows = rows.filter(
    (r) => r.status === 'ok' && r.action === 'update' && r.data,
  );
  const errorRows = rows.filter((r) => r.status === 'error');

  const handleDownloadErrors = () => {
    try {
      downloadUserEquipmentImportErrorRows(errorRows);
      toast({
        title: `Downloaded ${errorRows.length} error row(s)`,
        status: 'success',
        duration: 2500,
      });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    }
  };

  const mapPayload = (r) => ({
    phone: r.data.phone,
    userId: r.data.userId ?? null,
    equipmentTypeName: r.data.equipmentTypeName,
    equipmentTypeId: r.data.equipmentTypeId ?? null,
    manufacturer: r.data.manufacturer ?? null,
    model: r.data.model ?? null,
    variant: r.data.variant ?? null,
    fuel: r.data.fuel ?? null,
    color: r.data.color ?? null,
    regNo: r.data.regNo ?? null,
    chassisNumber: r.data.chassisNumber ?? null,
    engineNumber: r.data.engineNumber ?? null,
    capacity: r.data.capacity ?? null,
    capacityUom: r.data.capacityUom ?? null,
    yom: r.data.yom ?? null,
    nameAsPerRc: r.data.nameAsPerRc ?? null,
    description: r.data.description ?? null,
    rcValidUntil: r.data.rcValidUntil ?? null,
    pucValidUntil: r.data.pucValidUntil ?? null,
    insuranceValidUntil: r.data.insuranceValidUntil ?? null,
    fitnessValidUntil: r.data.fitnessValidUntil ?? null,
    taxValidUntil: r.data.taxValidUntil ?? null,
    isActive: r.data.isActive !== false,
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadUserEquipmentImportTemplate();
      toast({ title: 'Template downloaded', status: 'success', duration: 2000 });
    } catch (e) {
      toast({ title: e.message || 'Download failed', status: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setValidating(true);
    setPreview(null);
    setFileName(file.name);
    try {
      const result = await validateUserEquipmentImport(file);
      const withIds = {
        ...result,
        rows: (result?.rows || []).map((r) => ({
          ...r,
          id: `ue-${r.rowNumber}`,
        })),
      };
      setPreview(withIds);
      const createN = withIds.summary?.createValid ?? 0;
      const updateN = withIds.summary?.updateValid ?? 0;
      const inv = withIds.summary?.invalid ?? 0;
      toast({
        title:
          inv > 0
            ? `Validated: ${createN} new, ${updateN} existing, ${inv} error(s)`
            : `Validated: ${createN} new, ${updateN} already in system`,
        status: inv > 0 ? 'warning' : 'success',
        duration: 3500,
      });
    } catch (e) {
      setFileName('');
      toast({ title: e.message || 'Validation failed', status: 'error' });
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async (mode) => {
    const target = mode === 'update' ? existingRows : newRows;
    if (!target.length) return;
    const setBusy = mode === 'update' ? setUpdating : setAdding;
    setBusy(true);
    try {
      const result = await commitUserEquipmentImport({
        mode,
        rows: target.map(mapPayload),
      });
      toast({
        title:
          mode === 'update'
            ? `Updated ${result?.updated ?? 0} equipment row(s)`
            : `Added ${result?.created ?? 0} equipment row(s)`,
        status: 'success',
        duration: 3000,
      });
      const keepAction = mode === 'update' ? 'create' : 'update';
      const remaining = rows.filter(
        (r) => r.status === 'error' || r.action === keepAction,
      );
      if (!remaining.length) {
        setPreview(null);
        setFileName('');
      } else {
        setPreview({
          rows: remaining,
          summary: {
            total: remaining.length,
            valid: remaining.filter((r) => r.status === 'ok').length,
            invalid: remaining.filter((r) => r.status === 'error').length,
            createValid: remaining.filter(
              (r) => r.status === 'ok' && r.action === 'create',
            ).length,
            updateValid: remaining.filter(
              (r) => r.status === 'ok' && r.action === 'update',
            ).length,
          },
        });
      }
    } catch (e) {
      toast({ title: e.message || 'Save failed', status: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'rowNumber', label: 'Row', render: (r) => r.rowNumber },
    { key: 'status', label: 'Status', render: equipmentActionCell },
    { key: 'phone', label: 'Owner phone', render: (r) => r.data?.phone || '—' },
    {
      key: 'type',
      label: 'Type',
      render: (r) => r.data?.equipmentTypeName || '—',
    },
    { key: 'regNo', label: 'Reg no', render: (r) => r.data?.regNo || '—' },
    {
      key: 'makeModel',
      label: 'Make / model',
      render: (r) =>
        [r.data?.manufacturer, r.data?.model].filter(Boolean).join(' ') || '—',
    },
    { key: 'fuel', label: 'Fuel', render: (r) => r.data?.fuel || '—' },
    { key: 'errors', label: 'Errors', render: errorsCell },
  ];

  useHubHeaderActions(
    () => (
      <>
        <HubGhostButton
          leftIcon={<MdDownload size={16} />}
          onClick={handleDownload}
          isLoading={downloading}
        >
          Download template
        </HubGhostButton>
        <HubAmberButton
          leftIcon={<MdCloudUpload size={16} />}
          onClick={() => fileRef.current?.click()}
          isLoading={validating}
        >
          Upload Excel
        </HubAmberButton>
      </>
    ),
    [downloading, validating],
  );

  return (
    <>
      <Input
        ref={fileRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        display="none"
        onChange={handleFilePicked}
      />

      <ImportGuide
        steps={[
          'Download the template. phone = existing seller. equipmentTypeName = active type from master.',
          'After upload: New → Add new equipment. Same regNo + same owner → Update. Download error rows to fix.',
          'Photos / RC documents are not in Excel — upload them later on equipment detail.',
        ]}
        columns={[
          {
            column: 'phone',
            required: 'Required',
            unique: 'No',
            notes: 'Seller who can add catalog',
          },
          {
            column: 'equipmentTypeName',
            required: 'Required',
            unique: 'No',
            notes: 'Exact active type name',
          },
          {
            column: 'regNo',
            required: 'Required*',
            unique: 'Yes',
            notes: '*Usually required by type',
          },
          {
            column: 'manufacturer / model',
            required: 'Required*',
            unique: 'No',
            notes: '*When type requires them',
          },
          {
            column: 'fuel / color',
            required: 'Required*',
            unique: 'No',
            notes: '*When type requires them',
          },
          {
            column: 'Dates (RC / PUC / …)',
            required: 'Optional',
            unique: 'No',
            notes: 'DD/MM/YYYY or YYYY-MM-DD',
          },
        ]}
      />

      {preview ? (
        <Flex gap={3} flexWrap="wrap" my={3} align="center">
          <Text fontSize="13px" color={hub.muted}>
            File: {fileName || '—'}
          </Text>
          <Text fontSize="13px" fontWeight="600">
            {summary?.total ?? rows.length} total
          </Text>
          <Text fontSize="13px" fontWeight="600" color="green.600">
            New {summary?.createValid ?? newRows.length}
          </Text>
          <Text fontSize="13px" fontWeight="600" color="orange.500">
            Already in system {summary?.updateValid ?? existingRows.length}
          </Text>
          <Text fontSize="13px" color="red.400" fontWeight="600">
            {errorRows.length} with error
          </Text>
          <Box flex="1" />
          {errorRows.length > 0 ? (
            <HubGhostButton
              leftIcon={<MdDownload size={16} />}
              onClick={handleDownloadErrors}
            >
              Download error rows ({errorRows.length})
            </HubGhostButton>
          ) : null}
        </Flex>
      ) : (
        <Text fontSize="13px" color={hub.muted} my={3}>
          Upload a filled template to preview rows here.
        </Text>
      )}

      {newRows.length > 0 ? (
        <>
          <Flex align="center" gap={3} mb={2} flexWrap="wrap">
            <HubSectionLabel>New equipment ({newRows.length})</HubSectionLabel>
            <Box flex="1" />
            <HubAmberButton
              onClick={() => handleCommit('create')}
              isLoading={adding}
              isDisabled={updating}
            >
              Add new equipment
            </HubAmberButton>
          </Flex>
          <Box mb={4}>
            <HubTable columns={columns} rows={newRows} />
          </Box>
        </>
      ) : null}

      {existingRows.length > 0 ? (
        <>
          <Flex align="center" gap={3} mb={2} flexWrap="wrap">
            <HubSectionLabel>
              Already in system ({existingRows.length})
            </HubSectionLabel>
            <Box flex="1" />
            <HubAmberButton
              onClick={() => handleCommit('update')}
              isLoading={updating}
              isDisabled={adding}
            >
              Update these equipment
            </HubAmberButton>
          </Flex>
          <Box mb={4}>
            <HubTable columns={columns} rows={existingRows} />
          </Box>
        </>
      ) : null}

      {errorRows.length > 0 ? (
        <Box mb={2}>
          <HubSectionLabel>
            Rows with error ({errorRows.length})
          </HubSectionLabel>
          <HubTable columns={columns} rows={errorRows} />
        </Box>
      ) : null}
    </>
  );
}

export default HubBulkImportHub;
